import { useEffect, useRef, useState } from 'react';
import { useSession } from './session';
import './IdleLock.css';
import { useTranslation } from 'react-i18next';

/**
 * The reception computer, left open.
 *
 * After a few minutes with nobody touching it, the screen is covered: a
 * patient standing at the desk should not be able to read the record of the
 * one before them. Ported as the original had it — a curtain, not a second
 * sign-in. It does not log anybody out, because a receptionist who has to type
 * a password every time they turn round stops locking the screen at all.
 *
 * Being a curtain is the honest description: the page is still loaded
 * underneath. It protects against a passer-by, not against somebody who opens
 * the developer tools, and the session's own expiry is what protects against
 * that.
 */
export function IdleLock({ minutes }: { minutes: number }) {
  const { t } = useTranslation('common');
  const { logout } = useSession();
  const [locked, setLocked] = useState(false);
  // Not `useRef(Date.now())`: reading the clock during render is impure, and
  // the effect below is the first thing that runs anyway.
  const lastActive = useRef(0);
  const curtain = useRef<HTMLDivElement>(null);
  const primary = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (minutes <= 0) return;
    lastActive.current = Date.now();
    const touch = () => {
      lastActive.current = Date.now();
    };
    // `click` and `pointerdown` beside the original's four: a person driving
    // the screen by keyboard or assistive technology can produce a click
    // without ever producing a mousedown, and locking the screen under
    // somebody who is working is worse than locking it late.
    const events = ['mousedown', 'pointerdown', 'click', 'keydown', 'touchstart', 'wheel'] as const;
    for (const e of events) window.addEventListener(e, touch, { passive: true });

    const timer = window.setInterval(() => {
      if ((Date.now() - lastActive.current) / 60_000 >= minutes) setLocked(true);
    }, 30_000);

    return () => {
      for (const e of events) window.removeEventListener(e, touch);
      window.clearInterval(timer);
    };
  }, [minutes]);

  /*
   * A curtain that covers the page and not the keyboard.
   *
   * The overlay hid the application from the eye and nothing else: Tab walked
   * on through the menu and the record underneath, a screen reader read them
   * out, and Enter opened whatever had focus when the lock fell (PLAT-10).
   * So everything beside the curtain is made `inert` while it is shown — no
   * focus, no clicks, no accessibility tree — and focus is put on the
   * curtain's first button, where a keyboard user expects to find it.
   *
   * The siblings are found from the curtain itself rather than named by the
   * shell, so the lock does not depend on how the layout around it is built.
   * Only what this effect made inert is released, and focus goes back to
   * where it was, so unlocking returns the person to the field they left.
   */
  useEffect(() => {
    if (!locked) return;
    const own = curtain.current;
    const parent = own?.parentElement;
    if (!own || !parent) return;

    const before = document.activeElement as HTMLElement | null;
    // The attribute, not the `inert` property: the property only mirrors the
    // attribute where the browser implements it, and the attribute is what
    // says whether something was already inert before the lock fell.
    const madeInert = Array.from(parent.children).filter(
      (el) => el !== own && !el.hasAttribute('inert'),
    );
    for (const el of madeInert) el.setAttribute('inert', '');
    primary.current?.focus();

    return () => {
      for (const el of madeInert) el.removeAttribute('inert');
      if (before?.isConnected) before.focus();
    };
  }, [locked]);

  if (!locked) return null;

  return (
    <div
      ref={curtain}
      className="lock"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lock-title"
    >
      <div className="lock__card">
        <span className="lock__icon" aria-hidden>
          🔒
        </span>
        <h2 id="lock-title">{t('ecranBlocat')}</h2>
        <p>{t('sesiuneaAFostBlocata')}</p>
        <button
          ref={primary}
          type="button"
          className="lock__go"
          onClick={() => {
            lastActive.current = Date.now();
            setLocked(false);
          }}
        >
          {t('continuaLucrul')}
        </button>
        <button
          type="button"
          className="lock__out"
          onClick={() => {
            setLocked(false);
            void logout();
          }}
        >
          {t('iesiDinCont')}
        </button>
      </div>
    </div>
  );
}
